import {
  AppShellMain,
  Autocomplete,
  Button,
  createTheme,
  Fieldset,
  Input,
  MantineProvider,
  Menu,
  NavLink,
  Select,
  Table,
  Textarea,
} from "@mantine/core";
import { DatesProvider } from "@mantine/dates";
import { ModalsProvider } from "@mantine/modals";
import { Notifications, notifications } from "@mantine/notifications";
import { type PropsWithChildren, useEffect } from "react";
import { useMessage } from "../hooks/useProps";
import { ErrorState, getColorByState, getIconByState } from "../models/enums/errorState";

type UIProviderProps = PropsWithChildren;

export function UIProvider({ children }: UIProviderProps) {
  const message = useMessage();

  useEffect(() => {
    if (message?.title) {
      const color = getColorByState(message.state);
      const icon = getIconByState(message.state);
      notifications.show({
        title: message.title,
        icon,
        message: message.text,
        color,
        withCloseButton: message.state !== ErrorState.ERROR,
        autoClose: message.auto_close ?? 3500,
      });
    }
  }, [message]);

  return (
    <DatesProvider settings={{ locale: "de-ch" }}>
      <MantineProvider theme={docPortalTheme} withGlobalClasses forceColorScheme="light">
        <ModalsProvider>{children}</ModalsProvider>
        <Notifications />
      </MantineProvider>
    </DatesProvider>
  );
}

const docPortalTheme = createTheme({
  fontFamily: '"Source sans 3", sans-serif',
  primaryColor: "zli-blue",
  colors: {
    "zli-blue": ["#C4D4E5", "#E5F0F4", "#E0EDF2", "#8BAECA", "#518BB1", "#518BB1", "#518BB1", "#036D98", "#036D98", "#036D98"],
    "zli-green": ["#F0F8EA", "#E2F1D7", "#C6E5B1", "#AAD98C", "#8FCE69", "#83B81A", "#73A217", "#638B14", "#537511", "#435f0d"],
    "zli-orange": ["#FBE3CC", "#FAF0E6", "#F9EEE2", "#F7C699", "#F4AC66", "#F4AC66", "#F4AC66", "#F19034", "#F19034", "#F19034"],
    "zli-gray": ["#DADADA", "#DADADA", "#B2B2B2", "#B2B2B2", "#878787", "#878787", "#575757", "#575757", "#000000", "#000000"],
  },

  primaryShade: 7,

  defaultRadius: "xs",
  fontSizes: {
    sm: "1rem",
  },
  breakpoints: {
    xs: "40em",
    sm: "58em",
    md: "74em",
    lg: "84em",
    xl: "100em",
  },
  components: {
    AppShellMain: AppShellMain.extend({
      defaultProps: {
        bg: "#fbfbfb",
      },
    }),
    NavLink: NavLink.extend({
      defaultProps: {
        variant: "light",
        p: "sm",
      },
    }),
    Table: Table.extend({
      defaultProps: {
        verticalSpacing: "sm",
        horizontalSpacing: "md",
      },
    }),
    Button: Button.extend({
      defaultProps: {
        color: "zli-orange",
      },
    }),
    Input: Input.extend({
      defaultProps: {
        // maw: "60rem",
      },
    }),
    Textarea: Textarea.extend({
      defaultProps: {
        autosize: true,
      },
    }),
    Select: Select.extend({
      defaultProps: {
        nothingFoundMessage: "Nichts gefunden...",
        scrollAreaProps: { type: "always" },
      },
    }),
    Autocomplete: Autocomplete.extend({
      defaultProps: {
        scrollAreaProps: { type: "always" },
      },
    }),
    Fieldset: Fieldset.extend({
      styles: {
        legend: {
          fontSize: "1.2rem",
        },
      },
    }),
    Menu: Menu.extend({
      defaultProps: {
        shadow: "md",
      },
    }),
  },
});
